/**
 * De la hoja que trae la tienda a las filas que entiende el servidor.
 * Docs/15 §2.39.
 *
 * Las columnas se reconocen por su título, en cualquier orden y sin mirar
 * tildes ni mayúsculas: nadie llama igual a la columna del código de barras.
 * Lo que la hoja trae mal se dice en su fila, antes de preguntar al servidor;
 * lo que solo sabe el servidor —si ese código existe— lo dice la vista previa.
 */
import type { Tabla } from './hoja';
import { diaDeExcel, esNumeroDeExcel } from './hoja';
import { vencimientoDeLaCaja } from './vencimiento';

/** Una fila como la pide `POST /inventory/opening-stock`. */
export interface FilaDeCarga {
  row: number;
  code: string | null;
  name: string | null;
  quantity: string;
  unitCost: string;
  lotCode: string | null;
  expiresAt: string | null;
}

export type Columna =
  | 'code'
  | 'name'
  | 'quantity'
  | 'unitCost'
  | 'lotCode'
  | 'expiresAt'
  | 'minimum'
  | 'maximum';

/**
 * El mínimo y el máximo de la fila, si la hoja los trae. No viajan con la
 * carga —el servidor de la carga inicial no los conoce—: se ponen después,
 * ficha por ficha, con `setLimits`. Así la ferretería de dos mil productos no
 * tiene que entrar a cada uno a escribir su mínimo.
 */
export interface LimitesDeLaFila {
  minimum: string | null;
  maximum: string | null;
}

/** Los títulos que se reconocen para cada columna, ya sin tildes. */
const TITULOS: Record<Columna, readonly string[]> = {
  code: ['codigo de barras', 'cod barras', 'codigo', 'barras', 'ean', 'upc', 'sku', 'barcode', 'code'],
  name: ['nombre', 'producto', 'insumo', 'descripcion', 'articulo', 'item', 'name'],
  quantity: ['cantidad', 'stock', 'existencia', 'existencias', 'unidades', 'cant', 'quantity'],
  unitCost: ['costo unitario', 'costo', 'precio de compra', 'precio compra', 'costo sin igv', 'unit cost', 'cost'],
  lotCode: ['lote', 'numero de lote', 'lot'],
  expiresAt: ['vencimiento', 'fecha de vencimiento', 'vence', 'caducidad', 'f vencimiento', 'expira'],
  minimum: ['minimo', 'stock minimo', 'min', 'minimum'],
  maximum: ['maximo', 'stock maximo', 'max', 'maximum'],
};

function plegar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Dónde está cada columna en la cabecera. La que coincide entera gana a la
 * que solo empieza igual: «Código de barras» y «Código» en la misma hoja van
 * cada una a lo suyo.
 */
export function columnasDe(cabecera: readonly string[]): Partial<Record<Columna, number>> {
  const plegada = cabecera.map(plegar);
  const columnas: Partial<Record<Columna, number>> = {};
  const usadas = new Set<number>();

  for (const exacta of [true, false]) {
    for (const columna of Object.keys(TITULOS) as Columna[]) {
      if (columnas[columna] !== undefined) continue;
      for (const titulo of TITULOS[columna]) {
        const indice = plegada.findIndex(
          (celda, i) => !usadas.has(i) && (exacta ? celda === titulo : celda.startsWith(titulo)),
        );
        if (indice !== -1) {
          columnas[columna] = indice;
          usadas.add(indice);
          break;
        }
      }
    }
  }

  return columnas;
}

/** `"38,50"`, `"S/ 38.50"`, `"1.234,50"` → `"38.50"`, `"1234.50"`; nulo si no es un número. */
export function numeroDeLaHoja(texto: string): string | null {
  let limpio = texto.trim().replace(/^s\/\.?\s*/i, '').replace(/\s/g, '');
  if (limpio === '') return null;

  // Con los dos signos, el último es el decimal: «1.234,50» y «1,234.50».
  if (limpio.includes(',') && limpio.includes('.')) {
    limpio = limpio.lastIndexOf(',') > limpio.lastIndexOf('.')
      ? limpio.replace(/\./g, '').replace(',', '.')
      : limpio.replace(/,/g, '');
  } else {
    limpio = limpio.replace(',', '.');
  }

  return /^\d+(\.\d+)?$/.test(limpio) ? limpio : null;
}

/**
 * Si el número, tal como se escribió, se puede leer de dos maneras.
 *
 * «1.500» en el Perú son mil quinientos tornillos; leído como decimal es uno y
 * medio. Lo mismo «1,500» en una hoja en inglés. Un solo separador seguido de
 * exactamente tres cifras, con algo distinto de cero delante, no dice cuál
 * es. **Se pregunta en vez de adivinar**: equivocarse cambia el stock mil
 * veces, y corregir la celda cuesta un segundo. «0,250» (un cuarto de kilo) y
 * «1.5» no son ambiguos.
 */
export function ambiguo(texto: string): boolean {
  const limpio = texto.trim().replace(/^s\/\.?\s*/i, '').replace(/\s/g, '');
  return /^[1-9]\d{0,2}[.,]\d{3}$/.test(limpio);
}

/** Un vencimiento como lo escribe la gente, como día de calendario. */
export function diaDeLaHoja(texto: string): string | null {
  const limpio = texto.trim();
  if (limpio === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(limpio)) return valido(limpio);

  const latino = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(limpio);
  if (latino !== null) {
    const anio = latino[3]!.length === 2 ? `20${latino[3]}` : latino[3]!;
    return valido(`${anio}-${latino[2]!.padStart(2, '0')}-${latino[1]!.padStart(2, '0')}`);
  }

  // Solo mes y año, como viene en la caja —«EXP 11/2027», «11/27»—: vence el
  // último día de ese mes.
  const deLaCaja = vencimientoDeLaCaja(limpio);
  if (deLaCaja !== null) return deLaCaja;

  // Un número de días de Excel que no traía formato de fecha.
  if (/^\d{5}$/.test(limpio)) return diaDeExcel(Number(limpio));

  return null;
}

function valido(dia: string): string | null {
  const fecha = new Date(`${dia}T12:00:00Z`);
  return !Number.isNaN(fecha.getTime()) && fecha.toISOString().slice(0, 10) === dia ? dia : null;
}

/**
 * Las filas de la hoja, con lo que la hoja ya trae mal dicho fila por fila.
 *
 * Se salta lo vacío. Las filas con problema **no viajan**: la vista previa
 * las enseña con su motivo y la carga solo manda las que quedaron bien.
 */
export function filasDeLaHoja(tabla: Tabla): {
  columnas: Partial<Record<Columna, number>>;
  filas: { fila: FilaDeCarga; problemas: string[]; limites: LimitesDeLaFila | null }[];
  falta: string | null;
} {
  const indiceDeCabecera = tabla.findIndex((fila) => fila.some((celda) => celda.trim() !== ''));
  if (indiceDeCabecera === -1) {
    return { columnas: {}, filas: [], falta: 'La hoja está vacía.' };
  }

  const columnas = columnasDe(tabla[indiceDeCabecera]!);
  const falta =
    columnas.code === undefined && columnas.name === undefined
      ? 'Falta una columna con el código de barras o el nombre.'
      : columnas.quantity === undefined
        ? 'Falta la columna de la cantidad (Cantidad o Stock).'
        : columnas.unitCost === undefined
          ? 'Falta la columna del costo (Costo, sin IGV).'
          : null;
  if (falta !== null) return { columnas, filas: [], falta };

  const celda = (fila: readonly string[], columna: Columna): string => {
    const indice = columnas[columna];
    return indice === undefined ? '' : (fila[indice] ?? '').trim();
  };

  /** Lo escrito a mano que se lee de dos maneras; un número del Excel, nunca. */
  const dudoso = (fila: readonly string[], columna: Columna): boolean => {
    const indice = columnas[columna];
    return indice !== undefined && !esNumeroDeExcel(fila, indice) && ambiguo(celda(fila, columna));
  };

  /** Un mínimo o un máximo: vacío es «no lo cambies», no cero. */
  const limite = (
    fila: readonly string[],
    columna: 'minimum' | 'maximum',
    nombre: string,
    problemas: string[],
  ): string | null => {
    const dicho = celda(fila, columna);
    if (dicho === '') return null;
    if (dudoso(fila, columna)) {
      problemas.push(`¿El ${nombre} «${dicho}» es ${miles(dicho)} o ${decimal(dicho)}? Escríbelo sin punto o con coma decimal.`);
      return null;
    }
    const numero = numeroDeLaHoja(dicho);
    if (numero === null || !/^\d{1,9}(\.\d{1,3})?$/.test(numero)) {
      problemas.push(`El ${nombre} «${dicho}» no es una cantidad.`);
      return null;
    }
    return numero;
  };

  const filas = tabla.slice(indiceDeCabecera + 1).flatMap((fila, desplazamiento) => {
    if (fila.every((valor) => valor.trim() === '')) return [];

    const problemas: string[] = [];
    const cantidadDicha = celda(fila, 'quantity');
    const costoDicho = celda(fila, 'unitCost');
    const vencimientoDicho = celda(fila, 'expiresAt');

    const cantidad = dudoso(fila, 'quantity') ? null : numeroDeLaHoja(cantidadDicha);
    if (dudoso(fila, 'quantity')) {
      problemas.push(
        `¿«${cantidadDicha}» son ${miles(cantidadDicha)} o ${decimal(cantidadDicha)}? Escríbelo sin punto (${miles(cantidadDicha)}) o con coma decimal (${decimal(cantidadDicha)}).`,
      );
    } else if (cantidad === null) problemas.push(`La cantidad «${cantidadDicha}» no es un número.`);
    else if (!/^\d{1,9}(\.\d{1,3})?$/.test(cantidad)) {
      problemas.push('La cantidad lleva hasta tres decimales.');
    }

    const costo = dudoso(fila, 'unitCost') ? null : numeroDeLaHoja(costoDicho);
    if (dudoso(fila, 'unitCost')) {
      problemas.push(
        `¿El costo «${costoDicho}» es S/ ${miles(costoDicho)} o S/ ${decimal(costoDicho)}? Escríbelo sin punto de miles.`,
      );
    } else if (costo === null) problemas.push(`El costo «${costoDicho}» no es un número.`);
    else if (!/^\d{1,8}(\.\d{1,4})?$/.test(costo)) problemas.push('El costo lleva hasta cuatro decimales.');

    const minimo = limite(fila, 'minimum', 'mínimo', problemas);
    const maximo = limite(fila, 'maximum', 'máximo', problemas);
    if (minimo !== null && maximo !== null && Number(maximo) !== 0 && Number(maximo) < Number(minimo)) {
      problemas.push('El máximo no puede ser menor que el mínimo.');
    }

    const vence = vencimientoDicho === '' ? null : diaDeLaHoja(vencimientoDicho);
    if (vencimientoDicho !== '' && vence === null) {
      problemas.push(`El vencimiento «${vencimientoDicho}» no es una fecha: escríbelo como 31/03/2027.`);
    }

    return [
      {
        fila: {
          // La fila que se ve en Excel: la cabecera es la primera.
          row: indiceDeCabecera + desplazamiento + 2,
          code: celda(fila, 'code') || null,
          name: celda(fila, 'name') || null,
          quantity: cantidad ?? cantidadDicha,
          unitCost: costo ?? costoDicho,
          lotCode: celda(fila, 'lotCode') || null,
          expiresAt: vence,
        },
        problemas,
        limites: minimo === null && maximo === null ? null : { minimum: minimo, maximum: maximo },
      },
    ];
  });

  return { columnas, filas, falta: null };
}

/** «1.500» leído como miles: «1500». */
function miles(texto: string): string {
  return texto.trim().replace(/^s\/\.?\s*/i, '').replace(/[.,\s]/g, '');
}

/** «1.500» leído como decimal: «1,5». */
function decimal(texto: string): string {
  const [entero, fraccion] = texto.trim().replace(/^s\/\.?\s*/i, '').split(/[.,]/);
  const sinCeros = (fraccion ?? '').replace(/0+$/, '');
  return sinCeros === '' ? entero! : `${entero},${sinCeros}`;
}
